// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/connectors/setup/ConnectorSetupWizard.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";

const STEP_ORDER = ["gallery", "intro", "access", "credentials", "validate", "owner", "complete"];
const OPEN_COPILOT_EVENT = "oasys:open-copilot";

const STEP_LABELS = {
  gallery: "Choose",
  intro: "Overview",
  access: "Access",
  credentials: "Connection",
  validate: "Verify",
  owner: "Ownership",
  complete: "Complete",
};

const CATEGORY_LABELS = {
  all: "All",
  cloud: "Cloud",
  security: "Security",
  source_control: "Source Control",
  identity: "Identity",
  productivity: "Productivity",
};

const PROVIDER_CATEGORY_FALLBACKS = {
  aws: "cloud",
  cloudflare: "security",
  github: "source_control",
  google_workspace: "productivity",
  okta: "identity",
};

const PROVIDER_CATEGORY_ALIASES = {
  cloud_infrastructure: "cloud",
  edge_security: "security",
  identity_access: "identity",
  identity_productivity: "productivity",
};

function titleize(value) {
  return String(value || "")
    .replace(/[_.-]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatRequestDate(value) {
  if (!value) return "Not available";

  try {
    return new Date(value).toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "Not available";
  }
}

function userIdentifier(user) {
  return (
    user?.user_id ||
    user?.id ||
    user?.auth0_user_id ||
    user?.subject ||
    ""
  );
}

function userDisplayName(user) {
  const firstName = user?.first_name || user?.given_name || "";
  const lastName = user?.last_name || user?.family_name || "";
  const combinedName = `${firstName} ${lastName}`.trim();

  return (
    user?.name ||
    user?.display_name ||
    user?.full_name ||
    user?.user_name ||
    user?.username ||
    combinedName ||
    user?.email ||
    user?.email_address ||
    userIdentifier(user) ||
    "OASYS user"
  );
}

function userEmail(user) {
  return user?.email || user?.email_address || user?.primary_email || null;
}

function initialOwner(users) {
  return userIdentifier(users[0]);
}

function selectedOwner(users, ownerUserId) {
  return users.find((user) => userIdentifier(user) === ownerUserId) || null;
}

function valueForInput(inputs, key) {
  return inputs[key] ?? "";
}

function isConfirmationInput(field) {
  return field?.type === "checkbox";
}

function validationCheckPresentation(check) {
  const key = String(check?.key || "").trim().toLowerCase();
  const failed = check?.status !== "passed";

  if (failed && key === "role_arn_valid") {
    return {
      ...check,
      label: "Role ARN format",
      detail: "The Role ARN format is invalid.",
    };
  }

  return {
    ...check,
    label: check?.label || titleize(check?.key || "Provider verification"),
    detail:
      check?.detail ||
      (check?.status === "passed"
        ? "The provider completed this verification successfully."
        : "The provider could not complete this verification."),
  };
}

function providerWasContacted(validation) {
  return validation?.provider_contacted === true;
}

function liveValidationPassed(validation) {
  return (
    validation?.status === "validation_passed" &&
    validation?.connected === true &&
    providerWasContacted(validation) &&
    Boolean(validation?.verification_token)
  );
}

function readinessSummary(validation) {
  const passed = liveValidationPassed(validation);
  const contacted = providerWasContacted(validation);

  if (passed) {
    return {
      status: "passed",
      label: "Live provider verification passed",
      detail:
        "OASYS contacted the provider, verified the authorization, and received a trusted account identity.",
    };
  }

  if (contacted) {
    return {
      status: "warning",
      label: "Live provider verification failed",
      detail:
        "The provider was contacted, but the authorization, account identity, or trust relationship could not be verified.",
    };
  }

  return {
    status: "warning",
    label: "Provider verification not completed",
    detail:
      "Correct the required connection information and run verification again.",
  };
}

function remainingCompletionCriteria(criteria, validation) {
  const items = Array.isArray(criteria) ? criteria : [];

  if (!liveValidationPassed(validation)) {
    return items;
  }

  const remaining = items.filter((item) => {
    const normalized = String(item || "").trim().toLowerCase();

    return (
      normalized.includes("owner") ||
      normalized.includes("business purpose") ||
      normalized.includes("permission plan") ||
      normalized.includes("access plan")
    );
  });

  return remaining.length
    ? remaining
    : [
        "Connector owner assigned",
        "Business purpose confirmed",
      ];
}

function clearSensitiveInputs(detail, inputs) {
  const sensitiveKeys = new Set(
    (detail?.required_inputs || [])
      .filter((field) => field?.secret === true || field?.type === "password")
      .map((field) => field.key)
  );

  return Object.fromEntries(
    Object.entries(inputs || {}).map(([key, value]) => [
      key,
      sensitiveKeys.has(key) ? "" : value,
    ])
  );
}

function providerCategory(provider) {
  const explicitCategory = String(provider?.category || provider?.provider_category || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (explicitCategory) {
    return PROVIDER_CATEGORY_ALIASES[explicitCategory] || explicitCategory;
  }

  return PROVIDER_CATEGORY_FALLBACKS[provider?.provider_key] || "other";
}

function providerStatusLabel(provider) {
  if (provider?.status_label) return provider.status_label;

  const state = String(provider?.lifecycle_state || "")
    .trim()
    .toLowerCase();

  if (state === "healthy") return "Healthy";
  if (state === "connected") return "Connected";
  if (state === "degraded") return "Needs attention";
  if (state === "paused") return "Paused";
  if (state === "disconnected") return "Disconnected";
  if (state === "setup_started" || state === "setup_in_progress") return "Setup in progress";
  if (state === "reauthorization_required") return "Reauthorization required";
  return "Not connected";
}

function ProviderMark({ provider }) {
  const letters = String(provider?.display_name || provider?.provider_key || "O")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return <span className="enterprise-admin-connector-provider-mark">{letters}</span>;
}

export default function ConnectorSetupWizard({
  catalog,
  loading,
  error,
  users = [],
  actions = {},
  saving,
  onRefresh,
  onClose,
  initialProviderKey = null,
  reconnectMode = false,
  returnTo = null,
  returnStep = "connectors",
  embeddedContext = null,
  onCopilotContextChange = null,
}) {
  const providers = useMemo(() => catalog?.items || [], [catalog]);
  const [step, setStep] = useState("gallery");
  const [provider, setProvider] = useState(null);
  const [providerDetail, setProviderDetail] = useState(null);
  const [setupInputs, setSetupInputs] = useState({});
  const [validation, setValidation] = useState(null);
  const [verificationToken, setVerificationToken] = useState("");
  const [completed, setCompleted] = useState(null);
  const [ownerUserId, setOwnerUserId] = useState(() => initialOwner(users));
  const [businessPurpose, setBusinessPurpose] = useState("");
  const [riskNotes, setRiskNotes] = useState("");
  const [localError, setLocalError] = useState(null);
  const [working, setWorking] = useState(false);
  const [providerDetailLoading, setProviderDetailLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [requestViewOpen, setRequestViewOpen] = useState(false);
  const [requestSystemName, setRequestSystemName] = useState("");
  const [requestCategory, setRequestCategory] = useState("");
  const [requestBusinessPurpose, setRequestBusinessPurpose] = useState("");
  const [requestNotes, setRequestNotes] = useState("");
  const [requestSubmitted, setRequestSubmitted] = useState(null);
  const [requestHistoryOpen, setRequestHistoryOpen] = useState(false);
  const [requestHistory, setRequestHistory] = useState([]);
  const [requestHistoryLoading, setRequestHistoryLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [closeConfirmationOpen, setCloseConfirmationOpen] = useState(false);
  const setupScrollRef = useRef(null);
  const setupScrollPositionRef = useRef(0);
  const initialProviderOpenedRef = useRef(null);
  const previousViewRef = useRef({ step: "gallery", requestViewOpen: false, requestHistoryOpen: false });

  const activeDetail = providerDetail || provider;
  const owner = useMemo(() => selectedOwner(users, ownerUserId), [users, ownerUserId]);
  const disabled = saving || working;
  const currentStepIndex = STEP_ORDER.indexOf(step);
  const displayedValidationChecks = useMemo(
    () => (validation?.checks || []).map(validationCheckPresentation),
    [validation]
  );
  const displayedReadinessSummary = useMemo(
    () => readinessSummary(validation),
    [validation]
  );
  const displayedCompletionCriteria = useMemo(
    () => remainingCompletionCriteria(activeDetail?.completion_criteria, validation),
    [activeDetail, validation]
  );
  const returningToMissionLaunch = returnTo === "mission-launch";
  const embeddedInMissionLaunch = embeddedContext === "mission-launch";
  const activeLifecycleState = String(
    activeDetail?.lifecycle_state || provider?.lifecycle_state || ""
  ).trim().toLowerCase();
  const operationalReview = ["healthy", "connected"].includes(activeLifecycleState);
  const setupHasProgress =
    (!operationalReview &&
      Boolean(activeDetail) &&
      step !== "gallery" &&
      step !== "complete") ||
    (requestViewOpen && !requestSubmitted);

  const copilotProviderKey =
    activeDetail?.provider_key ||
    provider?.provider_key ||
    (step === "gallery" ? null : initialProviderKey) ||
    null;

  const showConnectorCopilotHelp =
    Boolean(activeDetail) &&
    !requestViewOpen &&
    !requestHistoryOpen &&
    step !== "gallery" &&
    step !== "complete";

  const connectorCopilotLabel =
    step === "credentials" ? "Need help? Ask OASYS" : "Ask OASYS";

  const openConnectorCopilot = () => {
    if (typeof window === "undefined") return;

    // The wizard already publishes provider + step through the governed Copilot
    // context callback above. This event only opens the existing global Copilot;
    // no form values, credentials, tokens, or secret material are attached.
    window.dispatchEvent(
      new CustomEvent(OPEN_COPILOT_EVENT, {
        detail: { source: "connector_setup" },
      })
    );
  };

  useEffect(() => {
    if (typeof onCopilotContextChange !== "function") return;

    // Intentionally report location only. setupInputs, verification tokens, and
    // every credential value remain inside the governed setup workflow and are
    // never copied into OASYS Copilot context.
    onCopilotContextChange({
      provider: copilotProviderKey,
      step,
      embeddedContext: embeddedContext || null,
    });
  }, [copilotProviderKey, embeddedContext, onCopilotContextChange, step]);

  const availableCategories = useMemo(() => {
    const present = new Set(providers.map(providerCategory));
    return ["all", ...Object.keys(CATEGORY_LABELS).filter((key) => key !== "all" && present.has(key))];
  }, [providers]);

  const filteredProviders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return providers.filter((item) => {
      const matchesCategory = category === "all" || providerCategory(item) === category;
      const searchable = [
        item.display_name,
        item.provider_key,
        item.description,
        item.setup_method,
        item.authorization_model,
        item.required_role,
        ...(item.evidence_capabilities || []),
        ...(item.expected_evidence_sources || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return matchesCategory && (!query || searchable.includes(query));
    });
  }, [category, providers, searchQuery]);

  const scrollSetupToTop = (behavior = "auto") => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setupScrollRef.current?.scrollTo({
          top: 0,
          behavior,
        });
      });
    });
  };


  useEffect(() => {
    if (!initialProviderKey || !providers.length || loading) return;
    if (initialProviderOpenedRef.current === initialProviderKey) return;

    const matched = providers.find((item) => item.provider_key === initialProviderKey);
    if (!matched) return;

    initialProviderOpenedRef.current = initialProviderKey;
    let cancelled = false;

    const openInitialProvider = async () => {
      setProvider(matched);
      setProviderDetail(null);
      setValidation(null);
      setVerificationToken("");
      setCompleted(null);
      setSetupInputs({});
      setBusinessPurpose(matched.description || "");
      setRiskNotes("");
      setLocalError(null);
      setRequestViewOpen(false);
      setRequestHistoryOpen(false);
      setSelectedRequest(null);
      setCloseConfirmationOpen(false);
      setStep(reconnectMode ? "credentials" : "intro");

      const lifecycleState = String(matched.lifecycle_state || "")
        .trim()
        .toLowerCase();
      const isOperational = ["healthy", "connected"].includes(lifecycleState);

      setProviderDetail(matched);

      if (isOperational) {
        setProviderDetailLoading(false);
        return;
      }

      try {
        setProviderDetailLoading(true);
        const detail = await actions.getConnectorSetupDetail?.(matched.provider_key);
        if (cancelled) return;
        setProviderDetail(detail ? { ...matched, ...detail } : matched);
        setBusinessPurpose(detail?.description || matched.description || "");
      } catch (err) {
        if (!cancelled) {
          setProviderDetail(matched);
          setLocalError(String(err?.message || err));
        }
      } finally {
        if (!cancelled) setProviderDetailLoading(false);
      }
    };

    openInitialProvider();
    return () => {
      cancelled = true;
    };
  }, [actions, initialProviderKey, loading, providers, reconnectMode]);

  useEffect(() => {
    if (step !== "owner") return;
    if (liveValidationPassed(validation) && verificationToken) return;
    setStep("validate");
  }, [step, validation, verificationToken]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    scrollSetupToTop();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const protectEscape = (event) => {
      if (event.key !== "Escape") return;

      event.preventDefault();
      event.stopPropagation();

      if (!closeConfirmationOpen) return;

      setCloseConfirmationOpen(false);
      window.requestAnimationFrame(() => {
        if (setupScrollRef.current) {
          setupScrollRef.current.scrollTop = setupScrollPositionRef.current;
        }
      });
    };

    window.addEventListener("keydown", protectEscape, true);

    return () => {
      window.removeEventListener("keydown", protectEscape, true);
    };
  }, [closeConfirmationOpen]);

  useEffect(() => {
    const previousView = previousViewRef.current;
    const viewChanged =
      previousView.step !== step ||
      previousView.requestViewOpen !== requestViewOpen ||
      previousView.requestHistoryOpen !== requestHistoryOpen;

    previousViewRef.current = { step, requestViewOpen, requestHistoryOpen };

    if (!viewChanged || closeConfirmationOpen) return;
    scrollSetupToTop();
  }, [step, requestViewOpen, requestHistoryOpen, closeConfirmationOpen]);

  const resetToGallery = () => {
    setProvider(null);
    setProviderDetail(null);
    setValidation(null);
    setVerificationToken("");
    setCompleted(null);
    setSetupInputs({});
    setBusinessPurpose("");
    setRiskNotes("");
    setLocalError(null);
    setWorking(false);
    setProviderDetailLoading(false);
    setRequestViewOpen(false);
    setRequestSystemName("");
    setRequestCategory("");
    setRequestBusinessPurpose("");
    setRequestNotes("");
    setRequestSubmitted(null);
    setRequestHistoryOpen(false);
    setRequestHistory([]);
    setSelectedRequest(null);
    setCloseConfirmationOpen(false);
    setStep("gallery");
  };

  const requestClose = () => {
    if (saving) return;

    if (setupHasProgress) {
      setupScrollPositionRef.current = setupScrollRef.current?.scrollTop || 0;
      setCloseConfirmationOpen(true);
      return;
    }

    onClose?.();
  };

  const continueSetup = () => {
    setCloseConfirmationOpen(false);

    window.requestAnimationFrame(() => {
      if (setupScrollRef.current) {
        setupScrollRef.current.scrollTop = setupScrollPositionRef.current;
      }
    });
  };

  const leaveSetup = () => {
    setCloseConfirmationOpen(false);
    onClose?.();
  };

  const chooseProvider = async (item) => {
    setProvider(item);
    setProviderDetail(null);
    setValidation(null);
    setVerificationToken("");
    setCompleted(null);
    setSetupInputs({});
    setBusinessPurpose(item.description || "");
    setRiskNotes("");
    setLocalError(null);
    setProviderDetailLoading(false);
    setRequestViewOpen(false);
    setRequestHistoryOpen(false);
    setSelectedRequest(null);
    setCloseConfirmationOpen(false);
    setStep("intro");

    const lifecycleState = String(item.lifecycle_state || "")
      .trim()
      .toLowerCase();
    const isOperational = ["healthy", "connected"].includes(lifecycleState);

    setProviderDetail(item);

    if (isOperational) {
      setProviderDetailLoading(false);
      return;
    }

    try {
      setProviderDetailLoading(true);
      const detail = await actions.getConnectorSetupDetail?.(item.provider_key);
      setProviderDetail(detail ? { ...item, ...detail } : item);
      setBusinessPurpose(detail?.description || item.description || "");
    } catch (err) {
      setProviderDetail(item);
      setLocalError(String(err?.message || err));
    } finally {
      setProviderDetailLoading(false);
    }
  };

  const startSetup = async () => {
    if (!activeDetail) return;
    setLocalError(null);
    setWorking(true);

    try {
      await actions.startConnectorSetup?.(activeDetail.provider_key, {
        reason: embeddedInMissionLaunch
          ? "Connector setup started from Mission Launch"
          : "Connector setup started from Enterprise Administration",
      });
      setStep("access");
    } catch (err) {
      setLocalError(String(err?.message || err));
    } finally {
      setWorking(false);
    }
  };

  const validateSetup = async () => {
    if (!activeDetail) return;
    setLocalError(null);
    setWorking(true);
    setVerificationToken("");

    try {
      const result = await actions.validateConnectorSetup?.(
        activeDetail.provider_key,
        setupInputs
      );

      setValidation(result);
      setVerificationToken(result?.verification_token || "");
      setSetupInputs((current) => clearSensitiveInputs(activeDetail, current));
      setStep("validate");
    } catch (err) {
      setValidation(null);
      setVerificationToken("");
      setSetupInputs((current) => clearSensitiveInputs(activeDetail, current));
      setLocalError(String(err?.message || err));
    } finally {
      setWorking(false);
    }
  };

  const completeSetup = async () => {
    if (!activeDetail) return;

    if (!liveValidationPassed(validation) || !verificationToken) {
      setLocalError("Run live provider verification again before completing this connector.");
      setStep("validate");
      return;
    }

    setLocalError(null);
    setWorking(true);

    try {
      const result = await actions.completeConnectorSetup?.(activeDetail.provider_key, {
        verification_token: verificationToken,
        owner_user_id: ownerUserId || null,
        owner_name: owner ? userDisplayName(owner) : null,
        owner_email: owner ? userEmail(owner) : null,
        business_purpose: businessPurpose,
        evidence_scope: activeDetail.required_scopes || [],
        framework_scope: [],
        risk_notes: riskNotes,
      });
      setCompleted(result);
      setVerificationToken("");
      await onRefresh?.();
      setStep("complete");
    } catch (err) {
      setLocalError(String(err?.message || err));
    } finally {
      setWorking(false);
    }
  };

  const submitConnectorRequest = async () => {
    setLocalError(null);
    setWorking(true);

    try {
      const result = await actions.submitConnectorRequest?.({
        system_name: requestSystemName.trim(),
        category: requestCategory,
        business_purpose: requestBusinessPurpose.trim(),
        notes: requestNotes.trim() || null,
      });
      setRequestSubmitted(result);
      scrollSetupToTop();
    } catch (err) {
      setLocalError(String(err?.message || err));
    } finally {
      setWorking(false);
    }
  };

  const openRequestHistory = async () => {
    setLocalError(null);
    setRequestViewOpen(false);
    setRequestHistoryOpen(true);
    setSelectedRequest(null);
    setRequestHistoryLoading(true);

    try {
      const result = await actions.getConnectorRequests?.(100);
      setRequestHistory(result?.items || []);
    } catch (err) {
      setLocalError(String(err?.message || err));
    } finally {
      setRequestHistoryLoading(false);
    }
  };

  const openRequestDetail = async (requestItem) => {
    setLocalError(null);
    setWorking(true);

    try {
      const result = await actions.getConnectorRequestDetail?.(requestItem.request_id);
      setSelectedRequest(result || requestItem);
    } catch (err) {
      setLocalError(String(err?.message || err));
    } finally {
      setWorking(false);
    }
  };

  const updateInput = (key, value) => {
    setSetupInputs((current) => ({ ...current, [key]: value }));
    setValidation(null);
    setVerificationToken("");
  };

  const returnToConnectionDetails = () => {
    setLocalError(null);
    setStep("credentials");
  };

  return (
    <div
      className="enterprise-admin-connector-setup-shell"
      role="dialog"
      aria-modal="true"
      aria-labelledby="connector-setup-title"
    >
      <div className="enterprise-admin-connector-setup-backdrop" aria-hidden="true" />

      <section
        className={[
          "enterprise-admin-connector-setup-card",
          operationalReview ? "operational-review" : "",
          closeConfirmationOpen ? "confirmation-open" : "",
          requestViewOpen ? "request-view-open" : "",
          requestHistoryOpen ? "request-history-open" : "",
        ].filter(Boolean).join(" ")}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          ref={setupScrollRef}
          className="enterprise-admin-connector-setup-scroll-region"
          aria-hidden={closeConfirmationOpen ? "true" : undefined}
        >
          <header className="enterprise-admin-connector-setup-header">
          <div>
            <p className="enterprise-admin-eyebrow">Connector Setup</p>
            <h2 id="connector-setup-title">
              {activeDetail
                ? operationalReview
                  ? `Review ${activeDetail.display_name} connection`
                  : `${reconnectMode ? "Reconnect" : "Connect"} ${activeDetail.display_name}`
                : "Connect your systems"}
            </h2>
            <p>
              {operationalReview
                ? "Review the active read-only connection without changing its provider authorization."
                : reconnectMode
                  ? "Reauthorize this disconnected connector with a fresh live provider verification."
                  : embeddedInMissionLaunch
                    ? "Authorize and verify this read-only connection without leaving your launch plan."
                    : "Authorize a read-only connection, verify it directly with the provider, assign ownership, and activate the connector."}
            </p>
          </div>
          <div className="enterprise-admin-connector-setup-header-actions">
            {showConnectorCopilotHelp ? (
              <button
                type="button"
                className={[
                  "enterprise-admin-connector-copilot-help",
                  step === "credentials"
                    ? "enterprise-admin-connector-copilot-help-emphasis"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={openConnectorCopilot}
                aria-controls="advisor-dock"
                title={`Ask OASYS Copilot about ${
                  activeDetail?.display_name || "this connector"
                } ${STEP_LABELS[step]?.toLowerCase() || "setup"}`}
              >
                <span aria-hidden="true">✦</span>
                {connectorCopilotLabel}
              </button>
            ) : null}

            <button
              type="button"
              className="enterprise-admin-connector-setup-close"
              onClick={requestClose}
              disabled={saving}
            >
              Close
            </button>
          </div>
        </header>

        {!requestViewOpen && !requestHistoryOpen ? (
          <div className="enterprise-admin-connector-setup-progress" aria-label="Connector setup progress">
            {STEP_ORDER.map((item, index) => {
              const complete = currentStepIndex > index;
              const active = step === item;
              const className = [active ? "active" : "", complete ? "complete" : ""]
                .filter(Boolean)
                .join(" ");

              return (
                <span key={item} className={className} aria-current={active ? "step" : undefined}>
                  {complete ? "✓ " : ""}{STEP_LABELS[item]}
                </span>
              );
            })}
          </div>
        ) : null}

        {error || localError ? (
          <div className="enterprise-admin-error compact">
            <strong>Connector setup unavailable</strong>
            <p>{error || localError}</p>
          </div>
        ) : null}

        {loading ? (
          <div className="enterprise-admin-empty compact enterprise-admin-connector-setup-loading-state">
            <strong>Loading connector setup…</strong>
            <p>This should only take a moment. You can close setup at any time.</p>
          </div>
        ) : null}

        {!loading && step === "gallery" && !requestViewOpen && !requestHistoryOpen ? (
          <div className="enterprise-admin-connector-gallery-surface">
            <div className="enterprise-admin-connector-gallery-toolbar">
              <label className="enterprise-admin-connector-gallery-search">
                <span>Search connectors</span>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search AWS, GitHub, identity, cloud…"
                />
              </label>

              <div className="enterprise-admin-connector-gallery-toolbar-actions">
                <button
                  type="button"
                  className="enterprise-admin-connector-submitted-button"
                  onClick={openRequestHistory}
                  disabled={disabled}
                >
                  Submitted requests
                </button>

                <div className="enterprise-admin-connector-gallery-count" aria-live="polite">
                  <strong>{filteredProviders.length}</strong>
                  <span>{filteredProviders.length === 1 ? "connector" : "connectors"}</span>
                </div>
              </div>
            </div>

            <div className="enterprise-admin-connector-gallery-filters" aria-label="Connector categories">
              {availableCategories.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={category === item ? "active" : ""}
                  onClick={() => setCategory(item)}
                >
                  {CATEGORY_LABELS[item] || titleize(item)}
                </button>
              ))}
            </div>

            <div className="enterprise-admin-connector-gallery enterprise-admin-connector-gallery-compact">
              {filteredProviders.map((item) => {
                const providerCategoryKey = providerCategory(item);

                return (
                  <button
                    key={item.provider_key}
                    type="button"
                    className="enterprise-admin-connector-gallery-card enterprise-admin-connector-gallery-row"
                    onClick={() => chooseProvider(item)}
                  >
                    <ProviderMark provider={item} />

                    <span className="enterprise-admin-connector-gallery-row-copy">
                      <span className="enterprise-admin-connector-gallery-row-title">
                        <strong>{item.display_name}</strong>
                        <span className="enterprise-admin-connector-gallery-category">
                          {CATEGORY_LABELS[providerCategoryKey] || titleize(providerCategoryKey)}
                        </span>
                        <em>{providerStatusLabel(item)}</em>
                      </span>

                      <span className="enterprise-admin-connector-gallery-description">
                        {item.description}
                      </span>

                      <small>{item.estimated_minutes} · {item.required_role}</small>
                    </span>

                    <span className="enterprise-admin-connector-gallery-select">
                      Select
                    </span>
                  </button>
                );
              })}

              <button
                type="button"
                className="enterprise-admin-connector-gallery-card enterprise-admin-connector-gallery-row enterprise-admin-connector-gallery-request-row request"
                onClick={() => {
                  setRequestSubmitted(null);
                  setRequestHistoryOpen(false);
                  setSelectedRequest(null);
                  setRequestViewOpen(true);
                }}
              >
                <span className="enterprise-admin-connector-request-mark" aria-hidden="true">+</span>

                <span className="enterprise-admin-connector-gallery-row-copy">
                  <span className="enterprise-admin-connector-gallery-row-title">
                    <strong>Don’t see your system?</strong>
                    <span className="enterprise-admin-connector-gallery-category">Request</span>
                  </span>

                  <span className="enterprise-admin-connector-gallery-description">
                    Request a connector for a system your organization relies on.
                  </span>

                  <small>No credentials or connection information are required.</small>
                </span>

                <span className="enterprise-admin-connector-gallery-select">
                  Request connector
                </span>
              </button>
            </div>

            {filteredProviders.length === 0 ? (
              <div className="enterprise-admin-connector-gallery-empty">
                <strong>No matching connectors</strong>
                <p>Try another search or request the system your organization uses.</p>
              </div>
            ) : null}
          </div>
        ) : null}


        {!loading && step === "gallery" && requestHistoryOpen ? (
          <div className="enterprise-admin-connector-setup-pane enterprise-admin-connector-request-history-pane">
            <div className="enterprise-admin-connector-request-history-header">
              <div>
                <p className="enterprise-admin-eyebrow">Submitted Requests</p>
                <h3>Your organization’s connector requests</h3>
                <p>
                  Review requests submitted by your tenant and open any request for its
                  complete business context and current status.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setRequestHistoryOpen(false);
                  setSelectedRequest(null);
                }}
                disabled={disabled}
              >
                Return to gallery
              </button>
            </div>

            {requestHistoryLoading ? (
              <div className="enterprise-admin-empty compact">
                <strong>Loading submitted requests…</strong>
                <p>Retrieving your tenant’s connector request history.</p>
              </div>
            ) : requestHistory.length === 0 ? (
              <div className="enterprise-admin-empty compact">
                <strong>No submitted requests yet</strong>
                <p>Your organization has not requested an additional connector.</p>
              </div>
            ) : (
              <div className="enterprise-admin-connector-request-history-layout">
                <div className="enterprise-admin-connector-request-history-list">
                  {requestHistory.map((item) => (
                    <button
                      key={item.request_id}
                      type="button"
                      className={[
                        "enterprise-admin-connector-request-history-item",
                        selectedRequest?.request_id === item.request_id ? "active" : "",
                      ].filter(Boolean).join(" ")}
                      onClick={() => openRequestDetail(item)}
                      disabled={working}
                    >
                      <span className="enterprise-admin-connector-request-history-main">
                        <strong>{item.system_name}</strong>
                        <small>{titleize(item.category)} · {formatRequestDate(item.created_at)}</small>
                      </span>
                      <span className={`enterprise-admin-connector-request-status status-${item.status || "submitted"}`}>
                        {titleize(item.status || "submitted")}
                      </span>
                    </button>
                  ))}
                </div>

                <aside className="enterprise-admin-connector-request-detail">
                  {selectedRequest ? (
                    <>
                      <div className="enterprise-admin-connector-request-detail-heading">
                        <p className="enterprise-admin-eyebrow">Request Detail</p>
                        <h3>{selectedRequest.system_name}</h3>
                        <span className={`enterprise-admin-connector-request-status status-${selectedRequest.status || "submitted"}`}>
                          {titleize(selectedRequest.status || "submitted")}
                        </span>
                      </div>

                      <dl>
                        <div>
                          <dt>Category</dt>
                          <dd>{titleize(selectedRequest.category)}</dd>
                        </div>
                        <div>
                          <dt>Requested by</dt>
                          <dd>{selectedRequest.requested_by?.name || "OASYS Administrator"}</dd>
                        </div>
                        <div>
                          <dt>Submitted</dt>
                          <dd>{formatRequestDate(selectedRequest.created_at)}</dd>
                        </div>
                        <div>
                          <dt>Request ID</dt>
                          <dd className="enterprise-admin-mono-wrap">{selectedRequest.request_id}</dd>
                        </div>
                      </dl>

                      <section>
                        <span>Business purpose</span>
                        <p>{selectedRequest.business_purpose}</p>
                      </section>

                      <section>
                        <span>Optional notes</span>
                        <p>{selectedRequest.notes || "No additional notes were provided."}</p>
                      </section>

                      <div className="enterprise-admin-connector-request-detail-guardrail">
                        <strong>Request only</strong>
                        <span>
                          This record does not authorize access, create a connector, or
                          store credentials.
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="enterprise-admin-empty compact">
                      <strong>Select a request</strong>
                      <p>Choose a submitted request to review its complete details.</p>
                    </div>
                  )}
                </aside>
              </div>
            )}
          </div>
        ) : null}

        {!loading && step === "gallery" && requestViewOpen ? (
          <div className="enterprise-admin-connector-setup-pane enterprise-admin-connector-request-pane">
            {!requestSubmitted ? (
              <form
                className="enterprise-admin-connector-request-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  submitConnectorRequest();
                }}
              >
                <div className="enterprise-admin-connector-request-copy">
                  <p className="enterprise-admin-eyebrow">Connector Request</p>
                  <h3>Bring another system into OASYS</h3>
                  <p>
                    Tell us which system your organization relies on and why it matters.
                    Do not include credentials, tokens, or connection details.
                  </p>
                </div>

                <div className="enterprise-admin-connector-request-fields">
                  <label>
                    <span>System name</span>
                    <input
                      value={requestSystemName}
                      onChange={(event) => setRequestSystemName(event.target.value)}
                      placeholder="Snowflake, ServiceNow, Salesforce…"
                      maxLength={120}
                      autoComplete="off"
                      required
                    />
                  </label>

                  <label>
                    <span>Category</span>
                    <select
                      value={requestCategory}
                      onChange={(event) => setRequestCategory(event.target.value)}
                      required
                    >
                      <option value="" disabled>Select category</option>
                      <option value="cloud">Cloud</option>
                      <option value="security">Security</option>
                      <option value="source_control">Source Control</option>
                      <option value="identity">Identity</option>
                      <option value="productivity">Productivity</option>
                      <option value="data">Data</option>
                      <option value="governance">Governance</option>
                      <option value="other">Other</option>
                    </select>
                  </label>

                  <label className="span-all">
                    <span>Business purpose</span>
                    <textarea
                      value={requestBusinessPurpose}
                      onChange={(event) => setRequestBusinessPurpose(event.target.value)}
                      placeholder="How would this connector support compliance, evidence collection, or operational governance?"
                      maxLength={500}
                      rows={2}
                      required
                    />
                  </label>

                  <label className="span-all">
                    <span>Optional notes</span>
                    <textarea
                      value={requestNotes}
                      onChange={(event) => setRequestNotes(event.target.value)}
                      placeholder="Relevant workflows, evidence needs, or organizational context"
                      maxLength={1000}
                      rows={2}
                    />
                  </label>
                </div>

                <div className="enterprise-admin-connector-request-footer">
                  <div className="enterprise-admin-connector-request-safety">
                    <strong>Request only</strong>
                    <span>
                      No connector, access authorization, secret storage, or integration
                      is created by this request.
                    </span>
                  </div>

                  <div className="enterprise-admin-connector-setup-actions enterprise-admin-connector-request-actions">
                    <button
                      type="button"
                      onClick={() => setRequestViewOpen(false)}
                      disabled={disabled}
                    >
                      Return to gallery
                    </button>
                    <button
                      type="submit"
                      disabled={
                        disabled ||
                        requestSystemName.trim().length < 2 ||
                        !requestCategory ||
                        requestBusinessPurpose.trim().length < 3
                      }
                    >
                      {working ? "Submitting…" : "Submit connector request"}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <div className="enterprise-admin-connector-request-success">
                <p className="enterprise-admin-eyebrow">Request Submitted</p>
                <h3>{requestSubmitted.system_name} has been recorded</h3>
                <p>
                  OASYS captured this request for integration planning. No connector was
                  created and no access was authorized.
                </p>

                <div className="enterprise-admin-connector-request-summary">
                  <article>
                    <span>Request status</span>
                    <strong>{titleize(requestSubmitted.status || "submitted")}</strong>
                  </article>
                  <article>
                    <span>Category</span>
                    <strong>{titleize(requestSubmitted.category)}</strong>
                  </article>
                  <article>
                    <span>Request ID</span>
                    <strong>{requestSubmitted.request_id}</strong>
                  </article>
                </div>

                <div className="enterprise-admin-connector-setup-actions">
                  <button type="button" onClick={resetToGallery}>
                    Return to gallery
                  </button>
                  <button type="button" onClick={openRequestHistory}>
                    View submitted requests
                  </button>
                  <button type="button" onClick={onClose}>
                    Close setup
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {activeDetail && step === "intro" ? (
          <div
            className={[
              "enterprise-admin-connector-setup-pane",
              "enterprise-admin-connector-intro-pane",
              operationalReview ? "enterprise-admin-connector-review-pane" : "",
            ].filter(Boolean).join(" ")}
            aria-busy={providerDetailLoading}
          >
            <h3>{activeDetail.display_name} setup</h3>
            <p>{activeDetail.description}</p>

            {operationalReview ? (
              <div className="enterprise-admin-connector-operational-review" role="status">
                <span aria-hidden="true">✓</span>
                <div>
                  <strong>Connection is active and healthy</strong>
                  <p>
                    This provider is already authorized for read-only evidence collection.
                    No setup changes are required for Mission Launch.
                  </p>
                </div>
              </div>
            ) : null}

            <div className="enterprise-admin-connector-setup-grid">
              <article>
                <span>Setup method</span>
                <strong>{titleize(activeDetail.setup_method)}</strong>
              </article>
              <article>
                <span>Authorization model</span>
                <strong>{titleize(activeDetail.authorization_model || activeDetail.setup_method)}</strong>
              </article>
              <article>
                <span>{operationalReview ? "Connection status" : "Estimated time"}</span>
                <strong>
                  {operationalReview
                    ? titleize(activeLifecycleState || "healthy")
                    : activeDetail.estimated_minutes}
                </strong>
              </article>
              <article>
                <span>Required role</span>
                <strong>{activeDetail.required_role}</strong>
              </article>
            </div>

            {activeDetail.administrator_prerequisites?.length ? (
              <div className="enterprise-admin-connector-access-list allowed">
                <strong>Administrator prerequisites</strong>
                {activeDetail.administrator_prerequisites.map((item) => (
                  <span key={item}>✓ {item}</span>
                ))}
              </div>
            ) : null}

            <div className="enterprise-admin-connector-external-id">
              <span>Current phase</span>
              <strong>
                {operationalReview
                  ? "Connected and ready for evidence collection"
                  : "Live provider authorization and verification"}
              </strong>
            </div>

            <div className="enterprise-admin-connector-setup-actions">
              <button type="button" onClick={resetToGallery} disabled={saving}>
                Back
              </button>

              {operationalReview ? (
                <button type="button" className="primary" onClick={onClose} disabled={saving}>
                  {embeddedInMissionLaunch ? "Continue launch plan" : "Return to Admin"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={startSetup}
                  disabled={disabled}
                >
                  {working ? "Starting…" : "Begin secure setup"}
                </button>
              )}
            </div>
          </div>
        ) : null}

        {activeDetail && step === "access" ? (
          <div className="enterprise-admin-connector-setup-pane two-column">
            <section>
              <p className="enterprise-admin-eyebrow">Access Review</p>
              <h3>Read-only by design</h3>
              <p>
                OASYS uses this connection for compliance evidence and operational posture.
                It does not perform destructive actions.
              </p>

              <div className="enterprise-admin-connector-access-list allowed">
                <strong>OASYS can</strong>
                {activeDetail.read_only_guarantees?.map((item) => <span key={item}>✓ {item}</span>)}
              </div>
            </section>

            <section>
              <p className="enterprise-admin-eyebrow">Blocked Actions</p>
              <h3>Protected boundaries</h3>
              <p>These actions remain outside this setup path and are explicitly guarded.</p>

              <div className="enterprise-admin-connector-access-list blocked">
                <strong>OASYS cannot</strong>
                {activeDetail.blocked_actions?.map((item) => <span key={item}>✕ {item}</span>)}
              </div>
            </section>

            <section>
              <p className="enterprise-admin-eyebrow">Evidence Intelligence</p>
              <h3>What this connector can support</h3>
              <p>These capabilities become available after this wizard successfully verifies and activates the live connection.</p>

              <div className="enterprise-admin-connector-access-list allowed">
                <strong>Evidence capabilities</strong>
                {activeDetail.evidence_capabilities?.map((item) => <span key={item}>✓ {item}</span>)}
              </div>

              <div className="enterprise-admin-connector-access-list allowed">
                <strong>Expected evidence sources</strong>
                {activeDetail.expected_evidence_sources?.map((item) => <span key={item}>• {item}</span>)}
              </div>
            </section>

            <section>
              <p className="enterprise-admin-eyebrow">Protected Boundaries</p>
              <h3>Never enabled by this workflow</h3>
              <p>The connector is verified live, while destructive and write-capable actions remain blocked.</p>

              <div className="enterprise-admin-connector-access-list blocked">
                <strong>Unavailable capabilities</strong>
                {activeDetail.unavailable_capabilities?.map((item) => <span key={item}>✕ {item}</span>)}
              </div>

              <div className="enterprise-admin-connector-access-list blocked">
                <strong>Common setup blockers</strong>
                {activeDetail.common_failure_conditions?.map((item) => <span key={item}>! {item}</span>)}
              </div>
            </section>

            <div className="enterprise-admin-connector-setup-actions span-all">
              <button type="button" onClick={() => setStep("intro")} disabled={saving}>Back</button>
              <button type="button" onClick={() => setStep("credentials")} disabled={disabled}>
                Continue securely
              </button>
            </div>
          </div>
        ) : null}

        {activeDetail && step === "credentials" ? (
          <form
            className="enterprise-admin-connector-setup-pane"
            onSubmit={(event) => {
              event.preventDefault();
              validateSetup();
            }}
          >
            <p className="enterprise-admin-eyebrow">Connection Details</p>
            <h3>{activeDetail.display_name} authorization details</h3>
            <p>
              Provide the authorization details required for a live provider verification.
              Sensitive values are submitted securely, cleared from this form after verification,
              and encrypted before the completed connector is persisted.
            </p>

            {activeDetail.external_id ? (
              <div className="enterprise-admin-connector-external-id">
                <span>Tenant external ID</span>
                <strong>{activeDetail.external_id}</strong>
              </div>
            ) : null}

            <div className="enterprise-admin-connector-field-grid">
              {activeDetail.required_inputs?.map((field) => (
                isConfirmationInput(field) ? (
                  <label key={field.key}>
                    <span>{field.label}</span>
                    <select
                      value={valueForInput(setupInputs, field.key) === true ? "true" : ""}
                      onChange={(event) => updateInput(field.key, event.target.value === "true")}
                      required={field.required !== false}
                    >
                      <option value="">Select confirmation</option>
                      <option value="true">Confirmed</option>
                    </select>
                  </label>
                ) : (
                  <label key={field.key}>
                    <span>{field.label}</span>
                    <input
                      type={field.type || "text"}
                      value={valueForInput(setupInputs, field.key)}
                      onChange={(event) => updateInput(field.key, event.target.value)}
                      placeholder={field.placeholder}
                      autoComplete="off"
                      required={field.required !== false}
                    />
                  </label>
                )
              ))}
            </div>

            <div className="enterprise-admin-connector-setup-grid">
              <article>
                <span>Read-only permission plan</span>
                <strong>{activeDetail.required_scopes?.join(" · ") || "Defined during authorization"}</strong>
              </article>
              <article>
                <span>Authorization state</span>
                <strong>Not connected</strong>
              </article>
              <article>
                <span>Secret handling</span>
                <strong>Encrypted at rest</strong>
              </article>
            </div>

            {activeDetail.operator_guidance?.length ? (
              <div className="enterprise-admin-connector-access-list allowed">
                <strong>Operator guidance</strong>
                {activeDetail.operator_guidance.map((item) => <span key={item}>• {item}</span>)}
              </div>
            ) : null}

            <div className="enterprise-admin-connector-setup-actions">
              <button type="button" onClick={() => setStep("access")} disabled={saving}>Back</button>
              <button type="submit" disabled={disabled}>{working ? "Contacting provider…" : "Verify live connection"}</button>
            </div>
          </form>
        ) : null}

        {activeDetail && step === "validate" ? (
          <div className="enterprise-admin-connector-setup-pane">
            <p className="enterprise-admin-eyebrow">Verification</p>
            <h3>{liveValidationPassed(validation) ? "Live connection verified" : "Connection verification failed"}</h3>
            <p>
              {liveValidationPassed(validation)
                ? `${activeDetail.display_name} was contacted successfully and returned a verified account identity.`
                : validation?.provider_contacted
                  ? `${activeDetail.display_name} was contacted, but the live authorization could not be verified.`
                  : "The provider was not contacted because required connection information is missing or invalid."}
            </p>

            <div className="enterprise-admin-connector-validation-list enterprise-admin-connector-validation-primary">
              {displayedValidationChecks.map((check) => (
                <article key={check.key} className={`status-${check.status}`}>
                  <span>{check.status === "passed" ? "✓" : "!"}</span>
                  <div>
                    <strong>{check.label}</strong>
                    <p>{check.detail}</p>
                  </div>
                </article>
              ))}
            </div>

            <div
              className={`enterprise-admin-connector-readiness-summary status-${displayedReadinessSummary.status}`}
            >
              <span>{displayedReadinessSummary.status === "passed" ? "✓" : "!"}</span>
              <div>
                <strong>{displayedReadinessSummary.label}</strong>
                <p>{displayedReadinessSummary.detail}</p>
              </div>
            </div>

            {liveValidationPassed(validation) ? (
              <div className="enterprise-admin-connector-external-id">
                <span>Verified provider account</span>
                <strong>
                  {validation?.verified_account?.name ||
                    validation?.verified_account?.reference ||
                    "Provider identity verified"}
                </strong>
              </div>
            ) : null}

            <div className="enterprise-admin-connector-verify-bottom-grid">
              {!liveValidationPassed(validation) ? (
                <div className="enterprise-admin-connector-external-id">
                  <span>Action required</span>
                  <strong>
                    Return to connection details, correct the authorization information, and run live verification again.
                  </strong>
                </div>
              ) : (
                <div className="enterprise-admin-connector-external-id">
                  <span>Next step</span>
                  <strong>Assign an accountable owner and confirm the business purpose.</strong>
                </div>
              )}

              {displayedCompletionCriteria.length ? (
                <div className="enterprise-admin-connector-access-list allowed">
                  <strong>Remaining completion requirements</strong>
                  {displayedCompletionCriteria.map((item) => (
                    <span key={item}>• {item}</span>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="enterprise-admin-connector-setup-actions">
              {liveValidationPassed(validation) ? (
                <>
                  <button
                    type="button"
                    onClick={returnToConnectionDetails}
                    disabled={saving}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep("owner")}
                    disabled={disabled || !verificationToken}
                  >
                    Assign owner
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={returnToConnectionDetails}
                    disabled={saving}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="primary"
                    onClick={returnToConnectionDetails}
                    disabled={disabled}
                  >
                    Correct connection details
                  </button>
                </>
              )}
            </div>
          </div>
        ) : null}

        {activeDetail && step === "owner" && liveValidationPassed(validation) && verificationToken ? (
          <form
            className="enterprise-admin-connector-setup-pane"
            onSubmit={(event) => {
              event.preventDefault();
              completeSetup();
            }}
          >
            <p className="enterprise-admin-eyebrow">Ownership</p>
            <h3>Assign an accountable owner</h3>
            <p>The provider connection is verified. Assign the accountable owner and business purpose before activation.</p>

            <div className="enterprise-admin-connector-field-grid">
              <label>
                <span>Connector owner</span>
                <select value={ownerUserId} onChange={(event) => setOwnerUserId(event.target.value)}>
                  <option value="">Unassigned</option>
                  {users.map((user) => {
                    const identifier = userIdentifier(user);

                    return (
                      <option key={identifier} value={identifier}>
                        {userDisplayName(user)}
                      </option>
                    );
                  })}
                </select>
              </label>

              <label>
                <span>Business purpose</span>
                <input
                  value={businessPurpose}
                  onChange={(event) => setBusinessPurpose(event.target.value)}
                  placeholder="Why this connector is authorized"
                  required
                />
              </label>

              <label className="span-all">
                <span>Risk notes</span>
                <input
                  value={riskNotes}
                  onChange={(event) => setRiskNotes(event.target.value)}
                  placeholder="Optional setup or governance notes"
                />
              </label>
            </div>

            <div className="enterprise-admin-connector-setup-actions">
              <button type="button" onClick={() => setStep("validate")} disabled={saving}>Back</button>
              <button
                type="submit"
                disabled={
                  disabled ||
                  !ownerUserId ||
                  !businessPurpose.trim() ||
                  !liveValidationPassed(validation)
                }
              >
                {working ? "Activating…" : "Activate connector"}
              </button>
            </div>
          </form>
        ) : null}

        {activeDetail && step === "complete" ? (
          <div className="enterprise-admin-connector-setup-pane complete">
            <ProviderMark provider={activeDetail} />
            <h3>{activeDetail.display_name} is connected</h3>
            <p>
              OASYS verified the provider authorization, activated the read-only connector,
              and recorded its accountable owner and business purpose in the governance registry.
            </p>

            <div className="enterprise-admin-connector-setup-grid">
              <article>
                <span>Status</span>
                <strong>{titleize(completed?.status || "setup_completed")}</strong>
              </article>
              <article>
                <span>Verified account</span>
                <strong>
                  {completed?.verified_account?.name ||
                    completed?.verified_account?.reference ||
                    "Provider identity verified"}
                </strong>
              </article>
              <article>
                <span>Governance</span>
                <strong>Active</strong>
              </article>
              <article>
                <span>Access model</span>
                <strong>Read-only verified</strong>
              </article>
              <article>
                <span>Live authorization</span>
                <strong>Verified</strong>
              </article>
              <article>
                <span>Next step</span>
                <strong>{titleize(completed?.next_step || activeDetail.governance_handoff?.next_step || "begin evidence collection")}</strong>
              </article>
            </div>

            {returningToMissionLaunch || embeddedInMissionLaunch ? (
              <div className="enterprise-admin-connector-mission-handoff" role="status">
                <span>Mission Launch</span>
                <strong>
                  {embeddedInMissionLaunch
                    ? "Connection complete. Your launch plan is ready to continue."
                    : "Connection complete. Return to your launch plan to continue."}
                </strong>
                <p>
                  {embeddedInMissionLaunch
                    ? "OASYS will refresh this provider and keep you on the Connectors step."
                    : `OASYS will refresh connector status and reopen the ${titleize(returnStep)} step.`}
                </p>
              </div>
            ) : null}

            <div className="enterprise-admin-connector-setup-actions">
              {!returningToMissionLaunch && !embeddedInMissionLaunch ? (
                <button type="button" onClick={resetToGallery}>
                  Add another connector
                </button>
              ) : null}
              <button type="button" className="primary" onClick={onClose}>
                {embeddedInMissionLaunch
                  ? "Continue launch plan"
                  : returningToMissionLaunch
                    ? "Return to Mission Launch"
                    : "Return to Admin"}
              </button>
            </div>
          </div>
        ) : null}

        </div>

        {closeConfirmationOpen ? (
          <div
            className="enterprise-admin-connector-close-confirmation"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="connector-setup-close-title"
            aria-describedby="connector-setup-close-description"
          >
            <div className="enterprise-admin-connector-close-confirmation-card">
              <p className="enterprise-admin-eyebrow">Setup in progress</p>
              <h3 id="connector-setup-close-title">Leave connector setup?</h3>
              <p id="connector-setup-close-description">
                Your live connection hasn’t been activated. Information entered during this setup session
                will be discarded.
              </p>
              <div className="enterprise-admin-connector-close-confirmation-actions">
                <button
                  type="button"
                  className="primary"
                  onClick={continueSetup}
                >
                  Continue setup
                </button>
                <button type="button" className="danger" onClick={leaveSetup}>
                  Leave setup
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
